import { FC, Fragment } from "react";
import dayjs from "dayjs";
import { MobileModal } from "@/components/Modal";
import Link from "next/link";
import {
  useCategoriesQuery,
  SortValues,
  useCoursesQuery,
  ProgressValues,
  useCourseProgressesQuery,
  usePaginatedItemsQuery,
  Category,
  Course,
} from "@/fetch/academy";
import Divider from "@/components/Divider";
import Typography from "@/components/Typography";
import Avatar, { AvatarGroup } from "@/components/Avatar";
import { MobilePaperTitle } from "@/components/PageTitle";
import { Skeleton } from "@/components/Loader";
import DotSeparator from "@/components/DotSeparator";
import styles from "./CategoryCoursesModal.module.scss";
import { INVISIBLE_CATEGORIES } from "./Constants";

type CourseDetailsProps = {
  course: Course;
  categoryName: string;
};

export const CourseDetails: FC<CourseDetailsProps> = ({
  course,
  categoryName,
}) => {
  const bannerImage = `https://res.cloudinary.com/gwatco/image/upload/f_auto,q_auto,h_180,dpr_2.0/${course.cover}.png`;
  const duration = dayjs
    .duration(course.total_length_in_min, "minutes")
    .format("H[h] m[m]")
    .replace(/\b0+[a-z]+\s*/gi, "")
    .trim();

  const avatarImageUrl = course.enrolments.sample_enrolments
    .slice(0, 4)
    .map(
      (each) =>
        `https://res.cloudinary.com/gwatco/image/upload/ar_1.0,c_thumb,g_auto/${each.s3_profile_pic}`
    );

  const visibleCategories = course.categories.filter(
    (eachCategory) =>
      !Boolean(INVISIBLE_CATEGORIES.find((each) => eachCategory.name === each))
  );

  const isInvisibleCategory = Boolean(
    INVISIBLE_CATEGORIES.find((each) => each === categoryName)
  );

  return (
    <Link href={`/academy/${course.id}`} passHref>
      <div className={styles.courseContainer} role="presentation">
        <div className={styles.courseDetails}>
          <Typography variant="body1">{course.title}</Typography>
          <Typography
            color="text.secondary"
            variant="body2"
            className={styles.description}
          >
            {
              // isInvisibleCategory
              //   ? visibleCategories?.[0].name || ""
              //   :
              categoryName
            }
            <DotSeparator />
            {duration}
          </Typography>
          <div className={styles.avatarsContainer}>
            <AvatarGroup
              imageUrls={avatarImageUrl}
              AvatarProps={{ size: "xs" }}
              className={styles.avatar}
            />
            {course.enrolments.total_enrolments - 4 > 0 && (
              <Typography
                color="text.secondary"
                variant="body2"
                className={styles.enrolmentCount}
              >
                {`+${course.enrolments.total_enrolments - 4}`}
              </Typography>
            )}
          </div>
        </div>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={bannerImage} alt={course.title} className={styles.image} />
      </div>
    </Link>
  );
};

export const CourseLoader = () => {
  return (
    <div className={styles.courseContainer}>
      <div className={styles.courseDetails}>
        <Skeleton variant="text" width={100} />
        <Skeleton variant="text" width={100} />
        <Skeleton variant="text" width={80} />
      </div>
      <Skeleton variant="rectangular" width={86} height={66} />
    </div>
  );
};

type Props = {
  categoryId: number;
  onClose: () => void;
};

const CategoryCoursesModal: FC<Props> = ({ categoryId, onClose }) => {
  const { data: categoriesData, isLoading: isLoadingCategories } =
    useCategoriesQuery();
  const categories = categoriesData?.response || [];
  const currentCategory = categories.find((each) => each.id === categoryId);

  const { data: coursesData, isLoading: isLoadingCourses } = useCoursesQuery({
    pageNum: 1,
    pageSize: 100,
    categoryIds: `${categoryId}`,
    sort: SortValues.added,
  });

  const courses = coursesData?.response.results || [];

  return (
    <MobileModal>
      <MobilePaperTitle
        title={currentCategory?.name || ""}
        iconType="close"
        onGoBack={onClose}
      />
      <div className={styles.container}>
        {isLoadingCourses
          ? [1, 2, 3].map((each, idx) => (
              <Fragment key={each}>
                <CourseLoader />
                {idx !== courses.length - 1 && (
                  <Divider className={styles.divider} />
                )}
              </Fragment>
            ))
          : courses.map((eachCourse, idx) => (
              <Fragment key={eachCourse.id}>
                <CourseDetails
                  course={eachCourse}
                  categoryName={currentCategory?.name || ""}
                />
                {idx !== courses.length - 1 && (
                  <Divider className={styles.divider} />
                )}
              </Fragment>
            ))}
      </div>
    </MobileModal>
  );
};

export default CategoryCoursesModal;
